import { useState, useEffect } from 'react';

// Extend Performance interface to include non-standard Chrome memory info
interface ExtendedPerformance extends Performance {
  memory?: {
    jsHeapSizeLimit: number;
    totalJSHeapSize: number;
    usedJSHeapSize: number;
  };
}

interface SystemInfo {
  browser: {
    userAgent: string;
    platform: string;
    language: string;
    cookiesEnabled: boolean;
    onLine: boolean;
    hardwareConcurrency: number;
    maxTouchPoints: number;
  };
  screen: {
    width: number;
    height: number;
    colorDepth: number;
    pixelDepth: number;
    orientation: string;
  };
  performance?: {
    memory?: {
      jsHeapSizeLimit: number;
      totalJSHeapSize: number;
      usedJSHeapSize: number;
    };
    timeOrigin: number;
    timing?: {
      navigationStart: number;
      loadEventEnd: number;
    };
  };
}

export const useSystemInfo = () => {
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const info: SystemInfo = {
        browser: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          cookiesEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine,
          hardwareConcurrency: navigator.hardwareConcurrency,
          maxTouchPoints: navigator.maxTouchPoints,
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          colorDepth: window.screen.colorDepth,
          pixelDepth: window.screen.pixelDepth,
          orientation: window.screen.orientation.type,
        },
      };

      // Add performance metrics if available
      if (window.performance) {
        const perf = performance as ExtendedPerformance;
        info.performance = {
          timeOrigin: perf.timeOrigin,
        };

        // Chrome-specific memory info
        if (perf.memory) {
          info.performance.memory = {
            jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
            totalJSHeapSize: perf.memory.totalJSHeapSize,
            usedJSHeapSize: perf.memory.usedJSHeapSize,
          };
        }

        // Navigation timing if available
        const timing = performance.timing;
        if (timing) {
          info.performance.timing = {
            navigationStart: timing.navigationStart,
            loadEventEnd: timing.loadEventEnd,
          };
        }
      }

      setSystemInfo(info);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch system info');
      setLoading(false);
    }
  }, []);

  return { systemInfo, loading, error };
};
